import React from 'react'
import moment from 'moment'
import Link from 'redux-first-router-link'
import NoticeBar from 'components/NoticeBar'
import { timeInHuman } from 'util/date'
import {
  SETTINGS_BILLING_PAGE,
  SETTINGS_INTEGRATIONS_PROVIDER_PAGE,
  SETTINGS_PREFERENCES_PAGE,
} from 'subapps/settings/types'

import GmailDisconnected from './GmailDisconnected'
import MessengerDisconnected from './MessengerDisconnected'
import ForwardingUnconfirmed from './ForwardingUnconfirmed'
import DemoMailboxBar from './DemoMailboxBar'
import SetupLegacy from './SetupLegacy'
import styles from './styles.less'

export default function AppNoticesView({
  showDemoMailboxBar,
  showExpiredBilling,
  showGitHubReauth,
  showPendingCancellation,
  cancelsAt,
  showCreditCardExpireSoon,
  showCreditCardExpired,
  creditCard,
  showLastBillingFailed,
  showTrialMessage,
  trialDaysLeft,
  showLegacyWidgetEOLNotice,
  isOwner,
  isAdmin,
  isCurrentMailboxConfirmedOrUnconfirmed,
  isCurrentMailboxDisconnected,
  disconnectedWidgets,
  isCurrentMailboxConfirmedOrUnconfirmedLegacy,
  showUpgradeToShopifyV2Banner,
  showInboxSidebarThemeLaunchNotice,
}) {
  const notices = []
  // The order of the notice below reflects their importance top -> most important
  if (showLastBillingFailed) {
    notices.push(
      <NoticeBar key="app-notice-billingfailed">
        We are unable to charge the credit card associated with your account.
        <Link
          className={styles.buttonLink}
          to={{ type: SETTINGS_BILLING_PAGE }}
        >
          Update credit card
        </Link>
      </NoticeBar>
    )
  }
  if (showExpiredBilling) {
    notices.push(
      <NoticeBar key="app-notice-expiredbilling" noticeFlagText="HEADS UP!">
        To continue using Groove please update your billing information.
      </NoticeBar>
    )
  }
  if (showPendingCancellation) {
    notices.push(
      <NoticeBar
        key="app-notice-pendingcancellation"
        flagName="app-notice-pendingcancellation"
        dismissible
        forceShow
      >
        Your account is scheduled to close on{' '}
        {timeInHuman(cancelsAt, { yearFormat: '%y' })}
      </NoticeBar>
    )
  }
  if (showCreditCardExpired) {
    notices.push(
      <NoticeBar key="app-notice-cardexpired">
        The credit card associated to your account has expired.
        <Link
          className={styles.buttonLink}
          to={{ type: SETTINGS_BILLING_PAGE }}
        >
          Update credit card
        </Link>
      </NoticeBar>
    )
  }
  if (showCreditCardExpireSoon) {
    const { expiryMonth, expiryYear } = creditCard
    const expireDate = moment(`${expiryYear}-${expiryMonth}-01`, 'YYYY-MM-DD')
    const flagName = `has_seen_credit_card_expire_soon_${expiryYear}_${expiryMonth}`
    notices.push(
      <NoticeBar
        key="app-notice-cardexpirysoon"
        flagName={flagName}
        forceShow
        dismissible
      >
        The credit card associated to your account will be expiring at the end
        of {expireDate.format('MMM YYYY')}.
        <Link
          className={styles.buttonLink}
          to={{ type: SETTINGS_BILLING_PAGE }}
        >
          Update credit card
        </Link>
      </NoticeBar>
    )
  }

  if (showDemoMailboxBar) {
    notices.push(
      <DemoMailboxBar
        key="app-notice-demomailbox"
        isManager={isOwner || isAdmin}
      />
    )
  }

  if (isCurrentMailboxConfirmedOrUnconfirmed) {
    notices.push(
      <ForwardingUnconfirmed
        key="app-notice-mailbox-confirmed-unconfirmed"
        isManager={isOwner || isAdmin}
      />
    )
  }

  if (isCurrentMailboxConfirmedOrUnconfirmedLegacy) {
    notices.push(
      <SetupLegacy
        key="app-notice-legacy-mailbox-confirmed-unconfirmed"
        isManager={isOwner || isAdmin}
      />
    )
  }

  if (showTrialMessage) {
    notices.push(
      <NoticeBar
        key="app-notice-trialinfo"
        flagName="app-notice-trialinfo"
        into
        dismissible
        forceShow
      >
        {trialDaysLeft <= 1 && 'You have less than a day left in your trial'}
        {trialDaysLeft > 1 &&
          trialDaysLeft < 2 &&
          'You have 1 day left in your trial'}
        {trialDaysLeft >= 2 &&
          `You have ${trialDaysLeft.toFixed(0)} days left in your trial`}
        <Link
          className={styles.buttonLink}
          to={{ type: SETTINGS_BILLING_PAGE }}
        >
          Subscribe now
        </Link>
      </NoticeBar>
    )
  }
  if (isCurrentMailboxDisconnected) {
    // KevinR: 2022-01-05
    // I know this breaks the convention of this file. The reason for this change
    // Is that this notice needs to open up a drawer using useDrawer. Hooks cannot
    // be conditionally called inside if statements, but by moving ths hook and the
    // code into a seperate component, I can effectively make the hook get called
    // conditionally within the "rules" of react
    notices.push(
      <GmailDisconnected
        key="app-notice-mailbox-disconnected"
        isManager={isOwner || isAdmin}
      />
    )
  }

  if (disconnectedWidgets.length > 0) {
    notices.push(
      <MessengerDisconnected
        key="app-notice-messenger-disconnect"
        isManager={isOwner || isAdmin}
        widget={disconnectedWidgets[0]}
      />
    )
  }

  if (showLegacyWidgetEOLNotice) {
    notices.push(
      <NoticeBar
        key="seen-app-notice-legacy-widget-eol"
        flagName="seen-app-notice-legacy-widget-eol"
        dismissible
        forceShow
      >
        {'\u{1F4E3}'}&nbsp;Still using our legacy widget? Time to&nbsp;
        <a
          className={styles.demoBarLinkInline}
          target="_blank"
          rel="noopener noreferrer"
          href="https://help.groovehq.com/help/migrating-from-the-legacy-widget-to-the-new-widget"
        >
          migrate
        </a>&nbsp; - it will be retired on November 19th.
      </NoticeBar>
    )
  }

  if (showGitHubReauth) {
    notices.push(
      <NoticeBar key="app-notice-githubReauth">
        The GitHub integration has been updated and needs to be re-installed.
        Read more about it in our{' '}
        <Link
          className={styles.demoBarLinkInline}
          target="_blank"
          to="https://help.groovehq.com/help/github-integration#existing-users"
        >
          KB article
        </Link>{' '}
        or go to the{' '}
        <Link
          className={styles.demoBarLinkInline}
          to={{
            type: SETTINGS_INTEGRATIONS_PROVIDER_PAGE,
            payload: { provider: 'github' },
          }}
        >
          integration settings
        </Link>{' '}
        to continue.
      </NoticeBar>
    )
  }

  if (showUpgradeToShopifyV2Banner) {
    notices.push(
      <NoticeBar
        key="seen-upgrade-to-shopifyV2-notice"
        flagName="seen-upgrade-to-shopifyV2-notice"
        isSessionFlag
        dismissible
      >
        Supercharge your workflow with our updated Shopify integration! Read
        our&nbsp;
        <a
          className={styles.demoBarLinkInline}
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.groovehq.com/blog/product-update-shopify-integration"
        >
          blog article
        </a>&nbsp;to learn more or see the&nbsp;
        <a
          className={styles.demoBarLinkInline}
          target="_blank"
          rel="noopener noreferrer"
          href="https://help.groovehq.com/help/upgrading-the-shopify-integration"
        >
          migration guide.
        </a>
      </NoticeBar>
    )
  }

  if (showInboxSidebarThemeLaunchNotice) {
    notices.push(
      <NoticeBar
        key="seen-app-notice-inbox-sidebar-theme-launch"
        flagName="seen-app-notice-inbox-sidebar-theme-launch"
        dismissible
        forceShow
      >
        {'\u{1F680}'}&nbsp; We&apos;ve launched a new sidebar with dark and
        light themes! You can update your appearance&nbsp;
        <Link
          className={styles.demoBarLinkInline}
          to={{ type: SETTINGS_PREFERENCES_PAGE }}
        >
          here.
        </Link>
      </NoticeBar>
    )
  }

  if (notices.length === 0) return null
  return notices.length > 0 ? notices[0] : null
}
